// Smart agenda-item tag renumbering.
//
// Staff use many numbering conventions, e.g.:
//   1, 2, 3, 4              (flat integers)
//   1.1, 1.2, 1.3          (fixed prefix, incrementing suffix)
//   75/21, 76/21, 77/21    (incrementing prefix, fixed "year" suffix)
//   1, 2-2                  (dash separated)
//   1, 2, 2.1, 2.2, 3      (hierarchical / nested)
//
// The engine detects the structure of the existing tags and renumbers the
// segment that varies down the list, preserving separators and any fixed
// segments (such as a year). Items whose tag has no digits are left untouched
// and do not participate in the sequence.

// Break a tag into an ordered list of numeric / separator tokens.
// "75/21" -> [{num 75}, {sep "/"}, {num 21}]
function tokenize(raw) {
    const parts = raw.match(/(\d+|\D+)/g) || [];
    return parts.map(p =>
        /^\d+$/.test(p)
            ? { type: "num", value: parseInt(p, 10), width: p.length }
            : { type: "sep", raw: p }
    );
}

// A signature describing the shape of the tag, ignoring the numeric values.
// "75/21" and "76/21" both -> "#/#"; "1.1" -> "#.#"; "3" -> "#".
function structureKey(tokens) {
    return tokens.map(t => (t.type === "num" ? "#" : t.raw)).join("");
}

function countNums(tokens) {
    return tokens.filter(t => t.type === "num").length;
}

function rebuild(tokens) {
    return tokens
        .map(t => (t.type === "num" ? String(t.value).padStart(t.width || 1, "0") : t.raw))
        .join("");
}

function numIndicesOf(tokens) {
    return tokens.map((t, i) => (t.type === "num" ? i : -1)).filter(i => i >= 0);
}

// Determine which numeric segment is the counter for a uniform-structure list.
function counterPosition(active) {
    const numIndices = numIndicesOf(active[0].toks);
    if (numIndices.length === 1) {
        return { counterPos: numIndices[0], numIndices };
    }
    // The counter is the numeric segment whose value differs across items.
    const varying = numIndices.filter(idx => {
        const values = active.map(p => p.toks[idx].value);
        return new Set(values).size > 1;
    });
    const counterPos =
        varying.length > 0
            ? varying[varying.length - 1]
            : numIndices[numIndices.length - 1];
    return { counterPos, numIndices };
}

// --- Trend verification -----------------------------------------------------
// Only renumber when the existing tags already progress predictably, so an
// automatic renumber will not clobber an intentional pattern we do not
// recognise (unforeseen variation).
function verifyTrendModeA(active) {
    const { counterPos, numIndices } = counterPosition(active);

    // Every non-counter numeric segment must genuinely be constant.
    for (const idx of numIndices) {
        if (idx === counterPos) continue;
        const values = active.map(p => p.toks[idx].value);
        if (new Set(values).size !== 1) return false;
    }

    // Counter values must form a strictly increasing arithmetic sequence. We
    // check them *sorted*, not in their current order, so that a reordered list
    // (which is temporarily out of order, e.g. 3,1,2,4 after dragging item 3 to
    // the top) is still recognised as renumberable and gets restored to a clean
    // sequence. Checking the current order would treat every reorder as an
    // "unrecognised" pattern and refuse to renumber.
    const seq = active
        .map(p => p.toks[counterPos].value)
        .slice()
        .sort((a, b) => a - b);
    if (seq.length >= 2) {
        const step = seq[1] - seq[0];
        if (step <= 0) return false; // duplicates or non-increasing: leave as-is
        for (let i = 1; i < seq.length; i++) {
            if (seq[i] - seq[i - 1] !== step) return false;
        }
    }
    return true;
}

function verifyTrendModeB(active) {
    // Hierarchical: only a single separator style may be used between levels.
    const seps = new Set();
    active.forEach(p => p.toks.forEach(t => { if (t.type === "sep") seps.add(t.raw); }));
    if (seps.size > 1) return false;

    // Depth may only step by at most +1 as we descend (no jumping levels).
    let prevDepth = 0;
    for (const p of active) {
        const depth = countNums(p.toks);
        if (depth <= 0) continue;
        if (prevDepth !== 0 && depth > prevDepth + 1) return false;
        prevDepth = depth;
    }
    return true;
}

// All tags share the same structure: exactly one segment is the counter, the
// rest are held fixed at the first item's values (e.g. the "/21" year).
function renumberModeA(active, startOverride) {
    const template = active[0].toks;
    const { counterPos, numIndices } = counterPosition(active);

    // Default start is the first item's own counter value (honours edits to the
    // first tag). Callers that reorder pass startOverride to preserve the
    // list's original starting number regardless of which item moved to the top.
    const startVal = startOverride != null ? startOverride : template[counterPos].value;
    const counterWidth = template[counterPos].width;

    active.forEach((p, i) => {
        const toks = template.map(t => ({ ...t }));
        // Hold the fixed numeric segments at the first item's values.
        numIndices.forEach(idx => {
            if (idx !== counterPos) {
                toks[idx].value = template[idx].value;
                toks[idx].width = template[idx].width;
            }
        });
        toks[counterPos].value = startVal + i;
        toks[counterPos].width = counterWidth;
        p.newTag = rebuild(toks);
    });
}

// Structures differ down the list: treat as hierarchical/nested numbering.
function renumberModeB(active, startOverride) {
    // Separator to use between nested levels, taken from a multi-segment item.
    let sep = ".";
    const multi = active.find(p => countNums(p.toks) > 1);
    if (multi) {
        const sepTok = multi.toks.find(t => t.type === "sep");
        if (sepTok) sep = sepTok.raw;
    }

    let counters = [];
    let initialised = false;

    active.forEach(p => {
        const depth = countNums(p.toks);
        if (depth <= 0) {
            p.newTag = p.raw;
            return;
        }

        if (!initialised) {
            // Seed from the first item's own numbers so lists can start at any
            // value and edits to the first tag are honoured. When reordering,
            // callers pass startOverride to preserve the original start.
            counters = p.toks.filter(t => t.type === "num").map(t => t.value);
            if (startOverride != null) {
                counters[0] = startOverride;
            }
            counters.length = depth;
            initialised = true;
        } else if (counters.length < depth) {
            while (counters.length < depth) counters.push(1); // going deeper: new level starts at 1
        } else {
            counters.length = depth; // same or shallower
            counters[depth - 1] = counters[depth - 1] + 1;
        }

        p.newTag = counters.slice(0, depth).join(sep);
        // Preserve a trailing separator the author used (e.g. "1." / "2."), which
        // the counter-join above would otherwise drop.
        const lastTok = p.toks[p.toks.length - 1];
        if (lastTok && lastTok.type === "sep") {
            p.newTag += lastTok.raw;
        }
    });
}

// Returns a new array of items with renumbered `tagEn` (and `tagCy` when it
// mirrored the English tag) and a resequenced `positionIndex`.
export function renumberTags(items, startOverride, force) {
    const parsed = items.map(it => {
        const raw = it.tagEn != null ? String(it.tagEn).trim() : "";
        const toks = /\d/.test(raw) ? tokenize(raw) : null;
        return { it, raw, toks, newTag: raw };
    });

    const active = parsed.filter(p => p.toks);
    let apply = false;
    if (active.length >= 2) {
        const keys = new Set(active.map(p => structureKey(p.toks)));
        if (keys.size === 1) {
            if (force || verifyTrendModeA(active)) {
                renumberModeA(active, startOverride);
                apply = true;
            }
        } else if (force || verifyTrendModeB(active)) {
            renumberModeB(active, startOverride);
            apply = true;
        }
    }

    return parsed.map((p, i) => {
        const updated = { ...p.it, positionIndex: i };
        if (apply && p.toks) {
            const mirroredCy =
                p.it.tagCy == null || String(p.it.tagCy).trim() === p.raw;
            updated.tagEn = p.newTag;
            if (mirroredCy) updated.tagCy = p.newTag;
        }
        return updated;
    });
}

// Resequence only `positionIndex` to match the current array order, leaving all
// tags untouched. Used when auto-numbering is disabled so reordering still
// persists the new order without rewriting the item numbers.
export function resequencePositions(items) {
    return items.map((it, i) => ({ ...it, positionIndex: i }));
}

// --- Grouping (primary + its subitems) --------------------------------------
// Returns the top-level starting number of a list (the first numbered item's
// first numeric segment), or null when there is no numbered item. Reorder
// handlers capture this BEFORE moving items so the list's original start can be
// preserved regardless of which item is dragged to the top.
export function startNumberOf(items) {
    for (const it of items) {
        const raw = it.tagEn != null ? String(it.tagEn).trim() : "";
        if (/\d/.test(raw)) {
            const first = raw.match(/\d+/);
            if (first) return parseInt(first[0], 10);
        }
    }
    return null;
}

// A subitem is one whose tag begins with a primary's whole tag followed by a
// separator (e.g. primary "10" -> "10-1", "10-2"; primary "1" -> "1.1").
// Returns an array of groups; each group is an array of consecutive items with
// the primary first followed by its subitems. Items that are not subitems each
// form their own single-element group. This lets a primary be dragged together
// with its subitems so their relative order is retained.
export function buildGroups(items) {
    const tag = it => (it.tagEn != null ? String(it.tagEn).trim() : "");

    const isChild = (childRaw, parentRaw) => {
        if (!parentRaw || !childRaw) return false;
        if (childRaw.length <= parentRaw.length) return false;
        if (!childRaw.startsWith(parentRaw)) return false;
        // The character after the parent prefix must be a separator, not an
        // alphanumeric (so "1" is not treated as the parent of "10").
        const next = childRaw.charAt(parentRaw.length);
        return !/[0-9a-z]/i.test(next);
    };

    const groups = [];
    let current = null;
    for (const it of items) {
        const raw = tag(it);
        if (current && isChild(raw, tag(current[0]))) {
            current.push(it);
        } else {
            current = [it];
            groups.push(current);
        }
    }
    return groups;
}

export default renumberTags;
